import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, Loader2, ShieldCheck, AlertTriangle, Copy, CheckCircle2, RefreshCw, Trash2, Hash,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Admin → Sales — payment reconciliation.
//
// Shows what the payment processor actually took against what is recorded here,
// so a payment that produced several sales (or none) is visible before anything
// is removed. Cleanup keeps the earliest sale for each payment, removes the
// surplus records and renumbers the surviving invoices into a clean numeric run.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function PaymentReconciliationModal({ onClose }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [daysBack, setDaysBack] = useState(30);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const scan = async () => {
    setScanning(true);
    setError('');
    setResult(null);
    try {
      const res = await base44.functions.invoke('reconcilePaymentDuplicates', { action: 'scan', daysBack });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Check failed');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not check the payments.');
      setData(null);
    }
    setScanning(false);
  };

  const cleanup = async () => {
    setCleaning(true);
    setError('');
    try {
      const res = await base44.functions.invoke('reconcilePaymentDuplicates', { action: 'cleanup', daysBack });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Cleanup failed');
      setResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders-for-stats'] });
      toast({
        title: `${res.data.removed.length} duplicate sale${res.data.removed.length !== 1 ? 's' : ''} removed`,
        description: res.data.renumbered.length
          ? `${res.data.renumbered.length} invoice number${res.data.renumbered.length !== 1 ? 's' : ''} renumbered`
          : undefined,
      });
      await scan();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not clean up the duplicates.');
    }
    setCleaning(false);
  };

  const duplicates = (data?.rows || []).filter((r) => r.extras?.length > 0);
  const missing = (data?.rows || []).filter((r) => r.order_count === 0);
  const misreferenced = (data?.rows || []).filter((r) => r.others?.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto">
      <div className="w-full max-w-4xl bg-[#111115] border border-white/10 my-auto">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-white/10">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <p className="text-primary text-[10px] font-bold tracking-widest">PAYMENT RECONCILIATION</p>
            <h2 className="text-white font-black text-lg uppercase">One Payment, One Sale</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 p-4 border-b border-white/5 flex-wrap">
          <label className="text-white/40 text-[10px] font-bold tracking-widest">LOOKBACK</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="bg-[#0d0d0f] border border-white/10 text-white text-xs px-2 py-1.5 focus:border-primary/60"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button
            onClick={scan}
            disabled={scanning || cleaning}
            className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors disabled:opacity-50"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} CHECK PAYMENTS
          </button>
          {data && (
            <span className="text-white/30 text-[10px] ml-auto">
              {data.payments_checked} payment{data.payments_checked !== 1 ? 's' : ''} checked
            </span>
          )}
        </div>

        <div className="p-5 space-y-5">
          {error && (
            <p className="flex items-start gap-2 border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-red-400 text-sm font-bold">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </p>
          )}

          {!data && !scanning && (
            <div className="text-center py-16 text-white/30">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-bold tracking-widest">NOTHING CHECKED YET</p>
              <p className="text-xs mt-1">
                Check the payments taken in the period against the sales recorded here.
              </p>
            </div>
          )}

          {scanning && (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {data && !scanning && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border border-white/10 bg-[#0d0d0f] px-3 py-3">
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Payments</p>
                  <p className="text-white font-black text-2xl mt-0.5">{data.payments_checked}</p>
                </div>
                <div className={`border px-3 py-3 ${data.duplicate_count ? 'border-red-500/40 bg-red-500/10' : 'border-white/10 bg-[#0d0d0f]'}`}>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Duplicated</p>
                  <p className={`font-black text-2xl mt-0.5 ${data.duplicate_count ? 'text-red-400' : 'text-white'}`}>
                    {data.duplicate_count}
                  </p>
                </div>
                <div className={`border px-3 py-3 ${data.surplus_orders ? 'border-amber-500/40 bg-amber-500/10' : 'border-white/10 bg-[#0d0d0f]'}`}>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Removable copies</p>
                  <p className={`font-black text-2xl mt-0.5 ${data.surplus_orders ? 'text-amber-300' : 'text-white'}`}>
                    {data.surplus_orders}
                  </p>
                </div>
                <div className={`border px-3 py-3 ${data.missing_count ? 'border-amber-500/40 bg-amber-500/10' : 'border-white/10 bg-[#0d0d0f]'}`}>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">No sale recorded</p>
                  <p className={`font-black text-2xl mt-0.5 ${data.missing_count ? 'text-amber-300' : 'text-white'}`}>
                    {data.missing_count}
                  </p>
                </div>
              </div>

              {/* Duplicated payments */}
              {duplicates.length > 0 && (
                <div>
                  <p className="flex items-center gap-2 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                    <Copy className="w-3.5 h-3.5" /> Same payment, same amount, written twice
                  </p>
                  <div className="border border-white/10 divide-y divide-white/5 max-h-[40vh] overflow-y-auto">
                    {duplicates.map((r) => (
                      <div key={r.payment_id} className="p-3">
                        <div className="flex items-start gap-3 flex-wrap">
                          <span className="font-mono text-[11px] font-bold text-orange-400">
                            …{r.payment_id.slice(-12)}
                          </span>
                          <span className="text-white/40 text-[11px]">{when(r.at)}</span>
                          <span className="text-[9px] font-bold tracking-widest border border-red-500/40 text-red-400 px-1.5 py-0.5">
                            {r.order_count} SALES
                          </span>
                          <span className="text-white font-black text-sm ml-auto">{fmt(r.amount)}</span>
                        </div>
                        <div className="mt-2 space-y-1 pl-1">
                          {r.keep && (
                            <p className="text-green-400 text-[11px] font-bold flex items-center gap-1.5">
                              <CheckCircle2 className="w-3 h-3" /> KEEP · invoice {r.keep.invoice_number || '—'} ·{' '}
                              {fmt(r.keep.amount)} · {when(r.keep.created_date)}
                            </p>
                          )}
                          {r.extras.map((x) => (
                            <p key={x.id} className="text-white/40 text-[11px] flex items-center gap-1.5">
                              <Trash2 className="w-3 h-3" /> REMOVE · invoice {x.invoice_number || '—'} ·{' '}
                              {fmt(x.amount)} · {when(x.created_date)}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Separate sales carrying a payment reference that isn't theirs */}
              {misreferenced.length > 0 && (
                <div>
                  <p className="flex items-center gap-2 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                    <AlertTriangle className="w-3.5 h-3.5" /> Separate sales sharing a payment reference
                  </p>
                  <div className="border border-amber-500/30 divide-y divide-white/5 max-h-[40vh] overflow-y-auto">
                    {misreferenced.map((r) => (
                      <div key={r.payment_id} className="p-3">
                        <div className="flex items-start gap-3 flex-wrap">
                          <span className="font-mono text-[11px] font-bold text-amber-300">
                            …{r.payment_id.slice(-12)}
                          </span>
                          <span className="text-white/40 text-[11px]">{when(r.at)}</span>
                          <span className="text-white font-black text-sm ml-auto">{fmt(r.amount)}</span>
                        </div>
                        <div className="mt-2 space-y-1 pl-1">
                          {r.others.map((x) => (
                            <p key={x.id} className="text-white/50 text-[11px]">
                              <span className="text-white/30">
                                {x.reason === 'same_basket' ? 'LINE OF THE SAME BASKET' : 'DIFFERENT SALE, LATER'}
                              </span>{' '}
                              · invoice {x.invoice_number || '—'} · {fmt(x.amount)} · {when(x.created_date)}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/30 text-[11px] mt-2">
                    These are real sales in their own right — the payment reference on them just doesn't belong to
                    them. Nothing is removed; they are listed so the attribution can be checked by hand.
                  </p>
                </div>
              )}

              {/* Payments with no sale */}
              {missing.length > 0 && (
                <div>
                  <p className="flex items-center gap-2 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                    <AlertTriangle className="w-3.5 h-3.5" /> Taken but with no sale recorded
                  </p>
                  <div className="border border-amber-500/30 divide-y divide-white/5">
                    {missing.map((r) => (
                      <div key={r.payment_id} className="flex items-center gap-3 p-3">
                        <span className="font-mono text-[11px] font-bold text-amber-300">…{r.payment_id.slice(-12)}</span>
                        <span className="text-white/40 text-[11px]">{when(r.at)}</span>
                        <span className="text-white font-black text-sm ml-auto">{fmt(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/30 text-[11px] mt-2">
                    These need looking at by hand — the money was taken but nothing was recorded against it.
                  </p>
                </div>
              )}

              {/* Shared invoice numbers */}
              {data.sharedInvoiceNumbers?.length > 0 && (
                <div>
                  <p className="flex items-center gap-2 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                    <Hash className="w-3.5 h-3.5" /> Invoice numbers used more than once
                  </p>
                  <div className="border border-amber-500/30 divide-y divide-white/5">
                    {data.sharedInvoiceNumbers.map((s) => (
                      <div key={s.number} className="flex items-center gap-3 p-3">
                        <span className="font-mono text-[11px] font-bold text-amber-300">{s.number}</span>
                        <span className="text-white/40 text-[11px]">
                          {s.orders.map((o) => `${o.customer_name || 'Customer'} ${fmt(o.amount)}`).join(' · ')}
                        </span>
                        <span className="text-white/30 text-[10px] font-bold ml-auto">{s.orders.length} SALES</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {duplicates.length === 0 && missing.length === 0 && misreferenced.length === 0 && !data.sharedInvoiceNumbers?.length && (
                <div className="text-center py-12 text-white/30 border border-white/5">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500/50" />
                  <p className="text-sm font-bold tracking-widest">EVERY PAYMENT MATCHES ONE SALE</p>
                  <p className="text-xs mt-1">Nothing to clean up in this period.</p>
                </div>
              )}

              {/* Cleanup */}
              {data.surplus_orders > 0 && !result && (
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white/40 text-[11px] mb-3">
                    Cleaning up keeps the sale that matches the payment, removes the{' '}
                    <span className="text-white font-bold">{data.surplus_orders} surplus cop{data.surplus_orders !== 1 ? 'ies' : 'y'}</span>{' '}
                    with their invoices, and renumbers the surviving invoices so the run is consecutive and unique again.
                    No other sale is touched.
                  </p>
                  <button
                    onClick={cleanup}
                    disabled={cleaning}
                    className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-black text-xs tracking-widest py-4 transition-colors"
                  >
                    {cleaning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {cleaning ? 'CLEANING UP…' : `REMOVE ${data.surplus_orders} DUPLICATE SALE${data.surplus_orders !== 1 ? 'S' : ''} AND RENUMBER`}
                  </button>
                </div>
              )}

              {result && (
                <div className="border border-green-500/40 bg-green-500/10 px-4 py-3">
                  <p className="flex items-center gap-2 text-green-400 font-bold text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Cleanup complete
                  </p>
                  <p className="text-green-300/80 text-xs mt-1.5">
                    {result.removed.length} duplicate sale{result.removed.length !== 1 ? 's' : ''} removed ·{' '}
                    {result.renumbered.length} invoice{result.renumbered.length !== 1 ? 's' : ''} renumbered
                    {result.invoices_regenerated ? ` · ${result.invoices_regenerated} PDF${result.invoices_regenerated !== 1 ? 's' : ''} regenerated` : ''}
                    {result.next_invoice_number ? ` · next invoice number ${result.next_invoice_number}` : ''}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-white/5 text-white/30 text-[10px]">
          From now on every payment route checks the processor's own payment reference before writing a sale, so a
          payment that has already been recorded returns the existing sale instead of creating another.
        </div>
      </div>
    </div>
  );
}